import { useState, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useEscKey } from "src/hooks/useEscKey";
import { TbFileText, TbX, TbCheck, TbAlertCircle, TbUpload, TbDownload } from "react-icons/tb";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { apiRequest } from "src/services/apiClient";
import { parseCsvPatients, type CsvPatientRow, type CsvParseResult } from "src/helpers/parseCsvPatients";
import "./csvImportModal.scss";

interface PreviewRow extends CsvPatientRow {
    _key: number;
    selected: boolean;
    isMinor: boolean;
    _parentRowIdx: number | null; // index in rows array of the recipient/parent
}

function ageFromDate(dateStr: string): number | null {
    if (!dateStr) return null;
    const born = new Date(dateStr);
    if (isNaN(born.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - born.getFullYear();
    const m = today.getMonth() - born.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < born.getDate())) age--;
    return age;
}

// Detect parent–child groups: same last name, minors share contact with an adult who has none.
// Transfers contact to the adult, marks them as recipient, and records the parent row index
// for each minor so the backend can set Patient.recipientId correctly.
export function postProcessFamilies(rows: CsvPatientRow[]): Array<CsvPatientRow & { _parentRowIdx: number | null }> {
    const ages = rows.map(r => ageFromDate(r.birthDate));
    const result: Array<CsvPatientRow & { _parentRowIdx: number | null }> =
        rows.map(r => ({ ...r, _parentRowIdx: null }));

    const byLastName = new Map<string, number[]>();
    rows.forEach((r, i) => {
        if (!r.lastName) return;
        const key = r.lastName.toLowerCase().trim();
        if (!byLastName.has(key)) byLastName.set(key, []);
        byLastName.get(key)!.push(i);
    });

    byLastName.forEach(indices => {
        if (indices.length < 2) return;
        const adultIdxs = indices.filter(i => ages[i] === null || ages[i]! >= 18);
        const minorIdxs = indices.filter(i => ages[i] !== null && ages[i]! < 18);
        if (adultIdxs.length === 0 || minorIdxs.length === 0) return;

        const parentI = adultIdxs.find(i => !result[i].email && result[i].tel.length === 0);
        const minorsWithContact = minorIdxs.filter(i => result[i].email || result[i].tel.length > 0);

        if (parentI !== undefined && minorsWithContact.length > 0) {
            const emails = [...new Set(minorsWithContact.map(i => result[i].email).filter(Boolean))];
            const tels  = [...new Set(minorsWithContact.flatMap(i => result[i].tel))];
            result[parentI] = { ...result[parentI], email: emails[0] ?? "", tel: tels, isRecipient: true };
            // Mark minors as patient-only and record which row is their parent
            minorIdxs.forEach(i => {
                result[i] = { ...result[i], isRecipient: false, _parentRowIdx: parentI };
            });
        }
    });

    // A minor should never default to being their own billing recipient just because their
    // CSV row happens to carry an email/phone (parseCsvPatients.ts sets isRecipient purely
    // from contact-info presence, with no age check) — the block above only clears that for
    // minors it could actually link to a same-surname adult in this file. When no such adult
    // exists (or the adult already has their own contact info), leave isRecipient false here
    // too, so it stays a forced manual "Change/Add Billing Recipient" step instead of quietly
    // importing the child as their own payer.
    result.forEach((r, i) => {
        if (ages[i] !== null && ages[i]! < 18 && r.isRecipient) {
            result[i] = { ...r, isRecipient: false };
        }
    });

    return result;
}

type Phase = "upload" | "preview" | "importing" | "done";

interface SkippedRow {
    firstName: string;
    lastName:  string;
    birthDate?: string;
    reason: "empty_name" | "duplicate_db" | "duplicate_csv";
}

interface ImportResult {
    created:            number;
    skipped:            number;
    skippedRows:        SkippedRow[];
    recipientsCreated?: number;
}

interface Props {
    onClose: () => void;
    onImported: () => void;
}

const IMPORT_STEPS = [
    "csvImport.stepParsing",
    "csvImport.stepValidating",
    "csvImport.stepCreating",
    "csvImport.stepFinalizing",
];

function CsvStep({ num, label, state }: { num: number; label: string; state: "idle" | "active" | "done" }) {
    return (
        <div className={`csv-modal__step csv-modal__step--${state}`}>
            <span className="csv-modal__step-num">
                {state === "done" ? <TbCheck /> : num}
            </span>
            <span className="csv-modal__step-label">{label}</span>
        </div>
    );
}

export default function CsvImportModal({ onClose, onImported }: Props) {
    const { t } = useTranslation();
    useEscKey(onClose, true);

    const [phase, setPhase]             = useState<Phase>("upload");
    const [isDrag, setIsDrag]           = useState(false);
    const [file, setFile]               = useState<File | null>(null);
    const [parseError, setParseError]   = useState<string | null>(null);
    const [rows, setRows]               = useState<PreviewRow[]>([]);
    const [parseMeta, setParseMeta]     = useState<Pick<CsvParseResult, "rawHeaders" | "mappedFields" | "unmappedHeaders"> | null>(null);
    const [progress, setProgress]       = useState(0);
    const [stepLabel, setStepLabel]     = useState("");
    const [result, setResult]           = useState<ImportResult | null>(null);
    const [allSelected, setAllSelected] = useState(true);
    const [showColInfo, setShowColInfo] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);

    const downloadTemplate = useCallback(() => {
        const headers = "Vorname;Nachname;Geburtsdatum;Geschlecht;Anrede;Titel;Straße;PLZ;Ort;Telefon;Mobil;E-Mail";
        const example = "Maria;Mustermann;15.06.1985;W;Frau;;Musterstraße 1;80333;München;089123456;0170123456;maria@beispiel.de";
        const bom = "﻿";
        const blob = new Blob([bom + headers + "\n" + example], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "patienten-vorlage.csv";
        a.click();
        URL.revokeObjectURL(url);
    }, []);

    // ── Parse CSV (called automatically on file selection) ─────────────────────

    const doParse = useCallback((f: File) => {
        const tryParse = (text: string) => {
            const parsed = parseCsvPatients(text);
            setParseMeta({ rawHeaders: parsed.rawHeaders, mappedFields: parsed.mappedFields, unmappedHeaders: parsed.unmappedHeaders });

            if (parsed.rows.length === 0) {
                setParseError(t("csvImport.errorEmpty"));
                setShowColInfo(parsed.rawHeaders.length > 0);
                return;
            }

            const hasAnyData = parsed.rows.some(
                (r) => r.firstName || r.lastName || r.birthDate || r.email || r.tel.length,
            );
            if (!hasAnyData) {
                setParseError(t("csvImport.errorNoColumns"));
                setShowColInfo(true);
                return;
            }

            const processed = postProcessFamilies(parsed.rows);
            setRows(processed.map((r, i) => ({
                ...r,
                _key: i,
                selected: true,
                isMinor: (ageFromDate(r.birthDate) ?? 99) < 18,
            })));
            setAllSelected(true);
            setParseError(null);
            setShowColInfo(false);
            setPhase("preview");
        };

        const reader = new FileReader();
        reader.onload = (e) => {
            const utf8Text = e.target?.result as string;
            if (/�/.test(utf8Text)) {
                const r2 = new FileReader();
                r2.onload = (e2) => {
                    try { tryParse(e2.target?.result as string); }
                    catch { setParseError(t("csvImport.errorParse")); }
                };
                r2.readAsText(f, "windows-1252");
            } else {
                try { tryParse(utf8Text); }
                catch { setParseError(t("csvImport.errorParse")); }
            }
        };
        reader.readAsText(f, "utf-8");
    }, [t]);

    // ── File handling ──────────────────────────────────────────────────────────

    const processFile = useCallback((f: File) => {
        if (!f.name.toLowerCase().endsWith(".csv")) {
            setParseError(t("csvImport.errorNotCsv"));
            return;
        }
        setFile(f);
        setParseError(null);
        setParseMeta(null);
        setShowColInfo(false);
        doParse(f); // auto-parse immediately — no extra button needed
    }, [t, doParse]);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) processFile(f);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) processFile(f);
    };

    // ── Preview controls ───────────────────────────────────────────────────────

    const toggleRow = (key: number) =>
        setRows((prev) => prev.map((r) => r._key === key ? { ...r, selected: !r.selected } : r));

    const toggleAll = () => {
        const next = !allSelected;
        setAllSelected(next);
        setRows((prev) => prev.map((r) => ({ ...r, selected: next })));
    };

    const selectedRows = rows.filter((r) => r.selected);

    // ── Import ─────────────────────────────────────────────────────────────────

    const handleImport = async () => {
        const toImport = selectedRows;
        if (toImport.length === 0) return;

        setPhase("importing");
        setProgress(0);

        // Animate progress through steps, then fire the real request
        const totalSteps = IMPORT_STEPS.length;
        let step = 0;

        const tick = () => {
            if (step < totalSteps - 1) {
                step++;
                setStepLabel(t(IMPORT_STEPS[step]));
                setProgress(Math.round((step / totalSteps) * 80));
            }
        };

        setStepLabel(t(IMPORT_STEPS[0]));
        const interval = setInterval(tick, 700);

        try {
            // _key equals the row's index in the full rows array (set as _key: i in tryParse).
            // Build a map from _key → position in the toImport array so the backend can resolve
            // familyRecipientRowIdx correctly even if some rows were deselected.
            const keyToImportIdx = new Map(toImport.map((r, i) => [r._key, i]));

            const data = await apiRequest<ImportResult>("/recipients/import-csv", {
                method: "POST",
                body: JSON.stringify({
                    rows: toImport.map(({ _key, selected, isMinor, _parentRowIdx, ...rest }) => ({
                        ...rest,
                        familyRecipientRowIdx: _parentRowIdx !== null
                            ? (keyToImportIdx.get(_parentRowIdx) ?? null)
                            : null,
                    })),
                }),
            }, { "Content-Type": "application/json" });
            clearInterval(interval);
            setProgress(100);
            setResult(data);
            setPhase("done");
            onImported();
        } catch {
            clearInterval(interval);
            setParseError(t("csvImport.errorImport"));
            setPhase("preview");
        }
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    const stepNum = phase === "done" ? 3 : phase === "upload" ? 1 : 2;

    return createPortal(
        <div className="csv-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className={`csv-modal csv-modal--${phase}`}>

                {/* Header */}
                <div className="csv-modal__header">
                    <div className="csv-modal__header-top">
                        <h2 className="csv-modal__title">{t("csvImport.title")}</h2>
                        <button className="csv-modal__close" onClick={onClose} aria-label="Schließen">
                            <TbX />
                        </button>
                    </div>
                    <div className="csv-modal__steps">
                        <CsvStep num={1} label={t("csvImport.stepLabelFile")}   state={stepNum > 1 ? "done"   : "active"} />
                        <div className={`csv-modal__step-line${stepNum > 1 ? " csv-modal__step-line--done" : ""}`} />
                        <CsvStep num={2} label={t("csvImport.stepLabelReview")} state={stepNum > 2 ? "done"   : stepNum === 2 ? "active" : "idle"} />
                        <div className={`csv-modal__step-line${stepNum > 2 ? " csv-modal__step-line--done" : ""}`} />
                        <CsvStep num={3} label={t("csvImport.stepLabelDone")}   state={stepNum === 3 ? "active" : "idle"} />
                    </div>
                </div>

                {/* Body */}
                <div className="csv-modal__body">

                    {/* ── Upload phase ── */}
                    {phase === "upload" && (
                        <>
                            <div
                                className={`csv-modal__drop-zone${isDrag ? " csv-modal__drop-zone--drag" : ""}`}
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setIsDrag(true); }}
                                onDragLeave={() => setIsDrag(false)}
                                onDrop={handleDrop}
                            >
                                <TbUpload />
                                <span className="csv-modal__drop-label">{t("csvImport.dropLabel")}</span>
                                <span className="csv-modal__drop-sub">{t("csvImport.dropSub")}</span>
                                <button
                                    type="button"
                                    className="csv-modal__file-btn"
                                    onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                                >
                                    {t("csvImport.browse")}
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    style={{ display: "none" }}
                                    onChange={handleFileChange}
                                />
                            </div>

                            {file && (
                                <div className="csv-modal__file-chosen">
                                    <TbFileText />
                                    {file.name}
                                </div>
                            )}

                            {parseError && (
                                <div className="csv-modal__parse-error">
                                    <TbAlertCircle /> {parseError}
                                </div>
                            )}

                            {showColInfo && parseMeta && parseMeta.rawHeaders.length > 0 && (
                                <div className="csv-modal__col-info">
                                    <div className="csv-modal__col-info-title">{t("csvImport.detectedCols")}</div>
                                    <div className="csv-modal__col-chips">
                                        {parseMeta.rawHeaders.map((h, i) => {
                                            const mapped = Object.entries(parseMeta.mappedFields).find(([, v]) => v === h);
                                            return (
                                                <span
                                                    key={i}
                                                    className={`csv-modal__col-chip csv-modal__col-chip--${mapped ? "mapped" : "unmapped"}`}
                                                    title={mapped ? t("csvImport.colMappedAs", { field: mapped[0] }) : t("csvImport.colIgnored")}
                                                >
                                                    {h}{mapped && <TbCheck />}
                                                </span>
                                            );
                                        })}
                                    </div>
                                    <p className="csv-modal__col-hint">{t("csvImport.noColsMatched")}</p>
                                </div>
                            )}

                            <div className="csv-modal__upload-footer">
                                <p className="csv-modal__parse-hint">{t("csvImport.hint")}</p>
                                <button type="button" className="csv-modal__template-btn" onClick={downloadTemplate}>
                                    <TbDownload /> {t("csvImport.downloadTemplate")}
                                </button>
                            </div>
                        </>
                    )}

                    {/* ── Preview phase ── */}
                    {phase === "preview" && (
                        <>
                            <p className="csv-modal__preview-intro">{t("csvImport.previewIntro")}</p>
                            <div className="csv-modal__preview-meta">
                                <strong>{rows.length}</strong> {t("csvImport.rowsFound")} ·{" "}
                                <strong>{selectedRows.length}</strong> {t("csvImport.rowsSelected")}
                                {parseMeta && (
                                    <button
                                        type="button"
                                        className="csv-modal__col-toggle"
                                        onClick={() => setShowColInfo((v) => !v)}
                                    >
                                        {showColInfo ? t("csvImport.hideColumns") : t("csvImport.showColumns")}
                                        {Object.keys(parseMeta.mappedFields).length === 0 && (
                                            <TbAlertCircle className="csv-modal__col-toggle-warn" />
                                        )}
                                    </button>
                                )}
                            </div>

                            {showColInfo && parseMeta && (
                                <div className="csv-modal__col-info">
                                    <div className="csv-modal__col-info-title">{t("csvImport.detectedCols")}</div>
                                    <div className="csv-modal__col-chips">
                                        {parseMeta.rawHeaders.map((h, i) => {
                                            const mapped = Object.entries(parseMeta.mappedFields).find(([, v]) => v === h);
                                            return (
                                                <span
                                                    key={i}
                                                    className={`csv-modal__col-chip${mapped ? " csv-modal__col-chip--mapped" : " csv-modal__col-chip--unmapped"}`}
                                                    title={mapped ? t("csvImport.colMappedAs", { field: mapped[0] }) : t("csvImport.colIgnored")}
                                                >
                                                    {h}
                                                    {mapped && <TbCheck />}
                                                </span>
                                            );
                                        })}
                                    </div>
                                    {parseMeta.unmappedHeaders.length > 0 && Object.keys(parseMeta.mappedFields).length === 0 && (
                                        <p className="csv-modal__col-hint">{t("csvImport.noColsMatched")}</p>
                                    )}
                                </div>
                            )}

                            {parseError && (
                                <div className="csv-modal__parse-error">
                                    <TbAlertCircle /> {parseError}
                                </div>
                            )}

                            <div className="csv-modal__table-wrap">
                                <table className="csv-modal__table">
                                    <thead>
                                        <tr>
                                            <th className="csv-modal__cb-th">
                                                <input
                                                    type="checkbox"
                                                    className="csv-modal__cb"
                                                    checked={allSelected}
                                                    onChange={toggleAll}
                                                    title={t("csvImport.toggleAll")}
                                                />
                                            </th>
                                            <th>{t("csvImport.colName")}</th>
                                            <th>{t("csvImport.colBornGender")}</th>
                                            <th>{t("csvImport.colAddress")}</th>
                                            <th>{t("csvImport.colContact")}</th>
                                            <th>{t("csvImport.colIsRecipient")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => {
                                            const fullName = [row.lastName, row.firstName].filter(Boolean).join(", ") || "—";
                                            const age = ageFromDate(row.birthDate);
                                            const agePart = age !== null ? ` (${age})` : "";
                                            const bornGender = [row.birthDate ? row.birthDate + agePart : "", row.gender].filter(Boolean).join(" · ") || "—";
                                            const rowClass = [
                                                !row.selected ? "csv-modal__row--deselected" : "",
                                                row._parentRowIdx !== null ? "csv-modal__row--child" : "",
                                            ].filter(Boolean).join(" ");
                                            return (
                                                <tr key={row._key} className={rowClass || undefined}>
                                                    <td className="csv-modal__cb-th">
                                                        <input
                                                            type="checkbox"
                                                            className="csv-modal__cb"
                                                            checked={row.selected}
                                                            onChange={() => toggleRow(row._key)}
                                                        />
                                                    </td>
                                                    <td className="csv-modal__cell-combined">
                                                        <span className="csv-modal__cell-main">{fullName}</span>
                                                        {row.isMinor && <span className="csv-modal__minor-badge">{t("csvImport.minor")}</span>}
                                                        {!row.isMinor && row.title && <span className="csv-modal__cell-sub">{row.title}</span>}
                                                    </td>
                                                    <td>{bornGender}</td>
                                                    <td className="csv-modal__cell-combined">
                                                        <span className="csv-modal__cell-main">{row.street || "—"}</span>
                                                        {(row.plz || row.city) && (
                                                            <span className="csv-modal__cell-sub">{[row.plz, row.city].filter(Boolean).join(" ")}</span>
                                                        )}
                                                    </td>
                                                    <td className="csv-modal__cell-combined">
                                                        <span className="csv-modal__cell-main">{row.tel.join(", ") || (row.email ? "" : "—")}</span>
                                                        {row.email && <span className="csv-modal__cell-sub">{row.email}</span>}
                                                    </td>
                                                    <td>
                                                        <span className={`csv-modal__rec-badge csv-modal__rec-badge--${row.isRecipient ? "yes" : "no"}`}>
                                                            {row.isRecipient ? <><TbCheck /> {t("csvImport.isRecipientYes")}</> : t("csvImport.isRecipientNo")}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}

                    {/* ── Importing phase ── */}
                    {phase === "importing" && (
                        <div className="csv-modal__progress-wrap">
                            <span className="csv-modal__progress-label">{stepLabel}</span>
                            <div className="csv-modal__progress-bar-track">
                                <div
                                    className="csv-modal__progress-bar-fill"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                    )}

                    {/* ── Done phase ── */}
                    {phase === "done" && result && (
                        <div className="csv-modal__result">
                            <IoMdCheckmarkCircle className="csv-modal__result-icon" />
                            <h3 className="csv-modal__result-title">{t("csvImport.doneTitle")}</h3>
                            <div className="csv-modal__result-stats">
                                <span>
                                    <TbCheck className="csv-modal__stat-icon--green" />
                                    {t("csvImport.doneCreated", { count: result.created })}
                                </span>
                                {result.skipped > 0 && (
                                    <span>
                                        <TbAlertCircle className="csv-modal__stat-icon--warn" />
                                        {t("csvImport.doneSkipped", { count: result.skipped })}
                                    </span>
                                )}
                                <span>
                                    <TbCheck className="csv-modal__stat-icon--green" />
                                    {t("csvImport.doneRecipients", { count: result.recipientsCreated ?? 0 })}
                                </span>
                            </div>
                            {result.created > 0 && (result.recipientsCreated ?? 0) === 0 && (
                                <p className="csv-modal__no-recipients-warn">
                                    {t("csvImport.warnNoRecipients")}
                                </p>
                            )}
                            {result.skippedRows?.length > 0 && (
                                <div className="csv-modal__skipped-list">
                                    <p className="csv-modal__skipped-label">{t("csvImport.skippedLabel")}</p>
                                    <ul>
                                        {result.skippedRows.map((r, i) => (
                                            <li key={i}>
                                                <span className="csv-modal__skipped-name">
                                                    {r.firstName || r.lastName
                                                        ? `${r.firstName} ${r.lastName}`.trim()
                                                        : t("csvImport.skippedNoName")}
                                                </span>
                                                {r.birthDate && (
                                                    <span className="csv-modal__skipped-bd">
                                                        {new Date(r.birthDate).toLocaleDateString()}
                                                    </span>
                                                )}
                                                <span className={`csv-modal__skipped-reason csv-modal__skipped-reason--${r.reason}`}>
                                                    {t(`csvImport.skipReason_${r.reason}`)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="csv-modal__footer">
                    {phase === "upload" && (
                        <button className="csv-btn csv-btn--ghost" onClick={onClose}>
                            {t("csvImport.cancel")}
                        </button>
                    )}

                    {phase === "preview" && (
                        <>
                            <button className="csv-btn csv-btn--ghost" onClick={() => { setPhase("upload"); setParseError(null); setParseMeta(null); setShowColInfo(false); }}>
                                {t("csvImport.back")}
                            </button>
                            <span className="csv-modal__footer-spacer" />
                            <button
                                className="csv-btn csv-btn--green"
                                onClick={handleImport}
                                disabled={selectedRows.length === 0}
                            >
                                {t("csvImport.import", { count: selectedRows.length })}
                            </button>
                        </>
                    )}

                    {phase === "importing" && (
                        <button className="csv-btn csv-btn--ghost" disabled>
                            {t("csvImport.importing")}
                        </button>
                    )}

                    {phase === "done" && (
                        <button className="csv-btn csv-btn--primary" onClick={onClose}>
                            {t("csvImport.close")}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
